import { fg } from '@atlaskit/platform-feature-flags';

import { type AggResponse, fetchAgg } from '../../../common/utils/fetch-agg';
import { inMemoryCache } from '../../../utils/caching/in-memory';

const GQL_ASSETS_SCHEMAS_NAME = 'SearchPageAssetsSchemasQuery';
const getAssetsSchemasQueryString = () => `query ${GQL_ASSETS_SCHEMAS_NAME}(
	$schemaARIs: [ID!]!) {
		assets_schemasByIds(ids: $schemaARIs) {
    		id
    		key
    		name
    		${fg('cdm_ga') ? 'displayName' : ''}
    		sequentialId @optIn(to: "AssetsApFields")
  		}
	}`;

export const assetsSchemasCache: {
	get: (key: string) => Promise<AggResponse<AssetsSchemasResponse>> | undefined;
	set: (key: string, result: Promise<AggResponse<AssetsSchemasResponse>>) => void;
	has: (key: string) => boolean;
	clear: (key?: string) => void;
	inMemoryDecorator: (
		key: string,
		supplier: () => Promise<AggResponse<AssetsSchemasResponse>>,
		expireWhen?: ((result: AggResponse<AssetsSchemasResponse>) => boolean) | undefined,
	) => Promise<AggResponse<AssetsSchemasResponse>>;
} = inMemoryCache<AggResponse<AssetsSchemasResponse>>();

export type AssetsSchemasQueryVariables = {
	schemaARIs: string[];
};

export type AssetsSchemasResponse = {
	assets_schemasByIds: {
		id: string;
		key: string;
		name: string;
		displayName?: string | null;
		sequentialId: string;
	}[];
};

export const assetsSchemasQuery: (
	schemaARIs: string[],
	headers?: Record<string, string>,
) => Promise<AggResponse<AssetsSchemasResponse>> = async (
	schemaARIs: string[],
	headers?: Record<string, string>,
) => {
	return assetsSchemasCache.inMemoryDecorator(`${schemaARIs}`, async () => {
		const response = await fetchAgg<AssetsSchemasQueryVariables, AssetsSchemasResponse>({
			graphQuery: getAssetsSchemasQueryString(),
			variables: {
				schemaARIs,
			},
			headers: {
				'X-ExperimentalApi': 'AssetsSchemas',
				...headers,
			},
		});

		const hasErrors = response.errors?.some((error) => error);

		if (hasErrors) {
			return Promise.reject(response);
		}

		return response;
	});
};
